import { HEN_BREEDS, BREED_IDS } from '../../data/breeds';
import { BOXES_PER_FLOCK } from '../../data/balance';
import type { BreedId } from '../../data/types';
import { buyNestBox, placeHen } from '../../core/actions';
import { henCost, nestBoxCost } from '../../core/economy';
import { formatMoney, formatRate } from '../../core/format';
import { previewHen } from '../../core/preview';
import { unlockProgress, unlockRuleText } from '../../core/requirements';
import { HEN_SPECIALS } from '../../core/specials';
import { ownedBreedCount } from '../../core/state';
import { game } from '../controller';
import { h, progressBar, setProgress, type Bindings } from '../dom';
import { henPortrait } from '../portrait';
import { buyButton, card, purchase, refreshShop, section, tag, type Panel } from '../shop';

type Sort = 'recommended' | 'cheapest' | 'income' | 'payback' | 'owned';
let sort: Sort = 'recommended';
let placing: number | null = null;

/** Called when the player clicks an empty nest on the canvas. */
export function placeInto(boxIndex: number): void {
  placing = boxIndex;
}

function sorted(ids: BreedId[], flockId: number): BreedId[] {
  const s = game.state;
  const c = game.ctx();
  const by = (f: (b: BreedId) => number) => [...ids].sort((a, b) => f(a) - f(b));
  switch (sort) {
    case 'cheapest': return by((b) => henCost(s, b, flockId));
    case 'income': return by((b) => -previewHen(s, c, flockId, b).gain);
    case 'payback': return by((b) => henCost(s, b, flockId) / Math.max(1e-9, previewHen(s, c, flockId, b).gain));
    case 'owned': return by((b) => -ownedBreedCount(s, b));
    default: return ids;
  }
}

export const hensPanel: Panel = {
  id: 'hens',
  label: 'Hens',
  badge: () => game.state.newBreeds.length,
  key: () => {
    const s = game.state;
    const f = game.viewFlock();
    // Rebuild only when something that changes the breed list or its previews changes.
    const hens = f.boxes.map((b) => b.hen?.breed ?? '-').join(',');
    return [s.viewFlock, s.unlockedBreeds.length, f.boxes.length, hens, sort, placing, s.newBreeds.length, s.upgrades.length, f.coop.length, f.rooster?.breed ?? '', JSON.stringify(s.infra)].join('|');
  },
  build(root: HTMLElement, b: Bindings) {
    const s = game.state;
    const c = game.ctx();
    const flock = game.viewFlock();
    if (placing !== null && (placing >= flock.boxes.length || flock.boxes[placing]?.hen)) placing = null;
    const empties = flock.boxes.filter((x) => !x.hen).length;
    const hens = flock.boxes.length - empties;

    const nests = section(`Nests in ${flock.name}`, `${hens} hens · ${flock.boxes.length}/${BOXES_PER_FLOCK} boxes`);
    if (flock.boxes.length < BOXES_PER_FLOCK) {
      const buy = buyButton({ cost: () => nestBoxCost(game.state, flock.id), onBuy: () => purchase(() => buyNestBox(game.state, flock.id)) }, b);
      nests.append(card({
        icon: '🪺',
        title: ['Nesting box'],
        desc: 'Every hen needs a nest. Buy the box, then pick a breed to fill it.',
        side: [buy.price, buy.btn],
      }));
    } else {
      nests.append(h('p', { class: 'hint', text: 'This flock is full (15 nests). Hire another rooster to start a new flock.' }));
    }
    if (placing !== null) {
      nests.append(h('div', { class: 'card highlight' },
        h('div', { class: 'body' }, h('div', { class: 'title' }, `Choose a hen for nest #${placing + 1}`)),
        h('button', { class: 'btn small secondary', type: 'button', onclick: () => { placing = null; refreshShop(true); } }, 'Cancel')));
    }
    root.append(nests);

    const list = section('Breeds');
    const sel = h('select', { 'aria-label': 'Sort breeds' },
      ...(['recommended', 'cheapest', 'income', 'payback', 'owned'] as Sort[]).map((v) =>
        h('option', { value: v, selected: v === sort }, { recommended: 'Recommended', cheapest: 'Cheapest', income: 'Most $/s here', payback: 'Fastest payback', owned: 'Most owned' }[v])));
    sel.addEventListener('change', () => { sort = sel.value as Sort; refreshShop(true); });
    list.append(h('div', { class: 'row', style: 'margin-bottom:8px' }, h('span', { class: 'hint', style: 'margin:0' }, 'Sort:'), sel));

    const unlocked = BREED_IDS.filter((id) => s.unlockedBreeds.includes(id));
    for (const id of sorted(unlocked, flock.id)) {
      const breed = HEN_BREEDS[id];
      const pv = previewHen(s, c, flock.id, id);
      const target = () => placing ?? flock.boxes.findIndex((x) => !x.hen);
      const buy = buyButton({
        cost: () => henCost(game.state, id, flock.id) + (flock.boxes.some((x) => !x.hen) ? 0 : nestBoxCost(game.state, flock.id)),
        enabled: () => flock.boxes.some((x) => !x.hen) || flock.boxes.length < BOXES_PER_FLOCK,
        label: () => (flock.boxes.some((x) => !x.hen) ? 'Buy' : 'Box + hen'),
        onBuy: () => purchase(() => {
          let idx = target();
          if (idx < 0) {
            const r = buyNestBox(game.state, flock.id);
            if (!r.ok) return r;
            idx = flock.boxes.length - 1;
          }
          const r = placeHen(game.state, game.ctx(), flock.id, idx, id);
          if (r.ok) placing = null;
          return r;
        }),
      }, b);
      const tags = [tag(breed.tier, `tier-${breed.tier}`)];
      if (s.newBreeds.includes(id)) tags.push(tag('new', 'new'));
      if (breed.broodyProne) tags.push(tag('broody'));
      if (pv.firstOfKind) tags.push(tag('★ first here +75%', 'star'));
      const special = breed.special ? HEN_SPECIALS[breed.special] : null;
      const details = h('details', {},
        h('summary', { class: 'meta' }, 'More'),
        special ? h('div', { class: 'desc' }, h('b', {}, `${special.name}: `), special.description) : null,
        h('div', { class: 'flavor' }, breed.flavor),
        h('div', { class: 'meta' }, `🐔 ${breed.birdFact}`));
      list.append(card({
        icon: henPortrait(id),
        title: [breed.name, ...tags],
        desc: `${pv.layRate.toFixed(2)} eggs/s × ${formatRate(pv.eggValue)} → +${formatRate(pv.gain)}/s here`,
        meta: `Owned: ${ownedBreedCount(s, id)} · ${breed.eggColor.replace('_', ' ')} eggs · ${breed.temperament}${breed.broodyProne ? ` · broodiness ${breed.broodiness}` : ''}`,
        side: [buy.price, buy.btn],
        extra: details,
      }));
    }

    const locked = BREED_IDS.filter((id) => !s.unlockedBreeds.includes(id)).slice(0, 4);
    if (locked.length) {
      const lockSec = section('Coming up', `${BREED_IDS.length - unlocked.length} breeds locked`);
      for (const id of locked) {
        const breed = HEN_BREEDS[id];
        const bar = progressBar(0);
        b.add(() => { const p = unlockProgress(game.state, breed.unlock); setProgress(bar, p.current / p.target); });
        lockSec.append(card({ cls: 'locked', icon: '🔒', title: [breed.name, tag(breed.tier, `tier-${breed.tier}`)], desc: unlockRuleText(breed.unlock), extra: bar }));
      }
      root.append(list, lockSec);
    } else {
      root.append(list);
    }
    // Viewing the tab clears the NEW badges.
    if (s.newBreeds.length) window.setTimeout(() => { game.state.newBreeds = []; }, 4000);
  },
};

export function formatHenCost(id: BreedId): string {
  return formatMoney(henCost(game.state, id, game.viewFlock().id));
}
